"use client";
// Anchored sibling of context-menu.tsx (click trigger instead of right-click).
// The root provides the same ContextMenuContext shape, so every item, label,
// separator and sub primitive from context-menu works here unchanged —
// they are re-exported below under Dropdown* names.

import {
  AnimatePresence,
  motion,
  useReducedMotion,
} from "framer-motion";
import {
  cloneElement,
  isValidElement,
  type HTMLAttributes,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactElement,
  type ReactNode,
  type Ref,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { EASE_OUT, SPRING_PANEL } from "./lib/ease";
import {
  ContextMenuContext,
  type ContextMenuContextValue,
  getEnabledItems,
} from "./context-menu";
import { cn } from "@/lib/utils";

const VIEWPORT_PADDING = 8;
const GAP = 6;

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

export interface DropdownMenuProps {
  children: ReactNode;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
}

export function DropdownMenu({
  children,
  open: controlledOpen,
  defaultOpen = false,
  onOpenChange,
  className,
}: DropdownMenuProps) {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const [activeId, setActiveId] = useState<string | null>(null);
  const controlled = controlledOpen !== undefined;
  const open = controlled ? controlledOpen : internalOpen;
  const triggerRef = useRef<HTMLElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const menuId = useId();
  const reduce = useReducedMotion() ?? false;

  const setOpen = useCallback(
    (next: boolean) => {
      if (!controlled) setInternalOpen(next);
      onOpenChange?.(next);
      if (!next) setActiveId(null);
    },
    [controlled, onOpenChange],
  );

  const value = useMemo<ContextMenuContextValue>(
    () => ({
      open,
      setOpen,
      // Anchored menus don't open at a point; the trigger drives open state.
      openAt: () => setOpen(true),
      point: { x: 0, y: 0 },
      modality: "keyboard",
      invocation: 0,
      menuId,
      triggerRef,
      contentRef,
      activeId,
      setActiveId,
      reduce,
    }),
    [open, setOpen, menuId, activeId, reduce],
  );

  return (
    <ContextMenuContext.Provider value={value}>
      <div className={cn("contents", className)}>{children}</div>
    </ContextMenuContext.Provider>
  );
}

type TriggerElementProps = HTMLAttributes<HTMLElement> & {
  ref?: Ref<HTMLElement>;
};

export interface DropdownMenuTriggerProps {
  children: ReactElement<TriggerElementProps>;
  disabled?: boolean;
  className?: string;
}

export function DropdownMenuTrigger({
  children,
  disabled = false,
  className,
}: DropdownMenuTriggerProps) {
  const context = useDropdownContext("DropdownMenuTrigger");

  if (!isValidElement(children)) {
    throw new Error("<DropdownMenuTrigger> requires a single React element");
  }

  const childProps = children.props;
  const childRef = children.props.ref;

  return cloneElement(children, {
    ref: (node: HTMLElement | null) => {
      context.triggerRef.current = node;
      assignRef(childRef, node);
    },
    "aria-controls": context.open ? context.menuId : undefined,
    "aria-haspopup": "menu",
    "aria-expanded": context.open,
    className: cn(childProps.className, className),
    onClick: (event: React.MouseEvent<HTMLElement>) => {
      childProps.onClick?.(event);
      if (event.defaultPrevented || disabled) return;
      context.setOpen(!context.open);
    },
    onKeyDown: (event: ReactKeyboardEvent<HTMLElement>) => {
      childProps.onKeyDown?.(event);
      if (event.defaultPrevented || disabled) return;
      if (event.key === "ArrowDown" || event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        context.setOpen(true);
      }
    },
  });
}

function assignRef<T>(ref: Ref<T> | undefined, value: T | null) {
  if (typeof ref === "function") {
    ref(value);
  } else if (ref) {
    ref.current = value;
  }
}

function useDropdownContext(component: string) {
  const context = useContext(ContextMenuContext);
  if (!context) {
    throw new Error(`${component} must be used within <DropdownMenu>`);
  }
  return context;
}

export interface DropdownMenuContentProps {
  children: ReactNode;
  className?: string;
  align?: "start" | "end";
  side?: "bottom" | "top";
  ariaLabel?: string;
}

export function DropdownMenuContent({
  children,
  className,
  align = "start",
  side = "bottom",
  ariaLabel = "Menu",
}: DropdownMenuContentProps) {
  const context = useDropdownContext("DropdownMenuContent");
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const typeahead = useRef("");
  const typeaheadTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useLayoutEffect(() => {
    if (!context.open) {
      setPosition(null);
      return;
    }
    const trigger = context.triggerRef.current;
    const content = context.contentRef.current;
    if (!trigger || !content) return;
    const t = trigger.getBoundingClientRect();
    const c = content.getBoundingClientRect();
    const x = clamp(
      align === "end" ? t.right - c.width : t.left,
      VIEWPORT_PADDING,
      Math.max(VIEWPORT_PADDING, window.innerWidth - c.width - VIEWPORT_PADDING),
    );
    // Flip upward when there isn't room below and there's more above.
    const below = window.innerHeight - t.bottom;
    const placeTop =
      side === "top" || (below < c.height + GAP + VIEWPORT_PADDING && t.top > below);
    const y = placeTop
      ? Math.max(VIEWPORT_PADDING, t.top - c.height - GAP)
      : Math.min(t.bottom + GAP, window.innerHeight - c.height - VIEWPORT_PADDING);
    setPosition({ x, y });
  }, [context.open, context.triggerRef, context.contentRef, align, side]);

  // Focus the first item once open (Radix parity for keyboard users).
  useEffect(() => {
    if (!context.open) return;
    const frame = requestAnimationFrame(() => {
      getEnabledItems(context.contentRef.current)[0]?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [context.open, context.contentRef]);

  // Outside press / scroll / resize closes.
  const { open, setOpen, triggerRef } = context;
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest?.("[data-context-menu-portal]")) return;
      if (triggerRef.current?.contains(target)) return;
      setOpen(false);
    };
    const close = () => setOpen(false);
    window.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("resize", close);
    window.addEventListener("scroll", close);
    return () => {
      window.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("resize", close);
      window.removeEventListener("scroll", close);
    };
  }, [open, setOpen, triggerRef]);

  useEffect(
    () => () => {
      if (typeaheadTimer.current) clearTimeout(typeaheadTimer.current);
    },
    [],
  );

  const moveFocus = (direction: 1 | -1) => {
    const items = getEnabledItems(context.contentRef.current);
    if (items.length === 0) return;
    const current = items.indexOf(document.activeElement as HTMLElement);
    const next = current < 0 ? 0 : (current + direction + items.length) % items.length;
    items[next]?.focus();
  };

  const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      context.setOpen(false);
      context.triggerRef.current?.focus();
      return;
    }
    if (event.key === "Tab") {
      context.setOpen(false);
      return;
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      moveFocus(event.key === "ArrowDown" ? 1 : -1);
      return;
    }
    if (event.key === "Home" || event.key === "End") {
      event.preventDefault();
      const items = getEnabledItems(context.contentRef.current);
      items[event.key === "Home" ? 0 : items.length - 1]?.focus();
      return;
    }
    // Typeahead, same behaviour as the context menu.
    if (
      event.key.length === 1 &&
      !event.ctrlKey &&
      !event.metaKey &&
      !event.altKey
    ) {
      typeahead.current += event.key.toLocaleLowerCase();
      if (typeaheadTimer.current) clearTimeout(typeaheadTimer.current);
      typeaheadTimer.current = setTimeout(() => {
        typeahead.current = "";
      }, 500);
      const match = getEnabledItems(context.contentRef.current).find((item) =>
        (item.dataset.label ?? item.textContent ?? "")
          .trim()
          .toLocaleLowerCase()
          .startsWith(typeahead.current),
      );
      match?.focus();
    }
  };

  return createPortal(
    <AnimatePresence>
      {context.open ? (
        <div
          data-context-menu-portal=""
          style={{
            left: position?.x ?? -9999,
            top: position?.y ?? -9999,
            visibility: position ? "visible" : "hidden",
          }}
          className="fixed z-[100]"
        >
          <motion.div
            ref={context.contentRef}
            id={context.menuId}
            role="menu"
            aria-label={ariaLabel}
            tabIndex={-1}
            initial={
              context.reduce
                ? { opacity: 0 }
                : { opacity: 0, scale: 0.96, y: side === "top" ? 4 : -4 }
            }
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={
              context.reduce
                ? { opacity: 0 }
                : { opacity: 0, scale: 0.97, transition: { duration: 0.12, ease: EASE_OUT } }
            }
            transition={context.reduce ? { duration: 0.08 } : SPRING_PANEL}
            style={{
              transformOrigin: `${align === "end" ? "right" : "left"} ${side === "top" ? "bottom" : "top"}`,
            }}
            onKeyDown={onKeyDown}
            onContextMenu={(event) => event.preventDefault()}
            className={cn(
              "min-w-48 overflow-hidden rounded-xl border border-border bg-card p-1.5 text-foreground shadow-xl outline-none",
              className,
            )}
          >
            {children}
          </motion.div>
        </div>
      ) : null}
    </AnimatePresence>,
    document.body,
  );
}

// Everything below is the context-menu primitive set, reused as-is — the
// DropdownMenu root above provides the context shape they consume.
export {
  ContextMenuItem as DropdownMenuItem,
  ContextMenuCheckboxItem as DropdownMenuCheckboxItem,
  ContextMenuRadioGroup as DropdownMenuRadioGroup,
  ContextMenuRadioItem as DropdownMenuRadioItem,
  ContextMenuLabel as DropdownMenuLabel,
  ContextMenuSeparator as DropdownMenuSeparator,
  ContextMenuShortcut as DropdownMenuShortcut,
  ContextMenuSub as DropdownMenuSub,
  ContextMenuSubTrigger as DropdownMenuSubTrigger,
  ContextMenuSubContent as DropdownMenuSubContent,
} from "./context-menu";

export type {
  ContextMenuItemProps as DropdownMenuItemProps,
  ContextMenuCheckboxItemProps as DropdownMenuCheckboxItemProps,
  ContextMenuRadioGroupProps as DropdownMenuRadioGroupProps,
  ContextMenuRadioItemProps as DropdownMenuRadioItemProps,
  ContextMenuLabelProps as DropdownMenuLabelProps,
  ContextMenuSeparatorProps as DropdownMenuSeparatorProps,
  ContextMenuShortcutProps as DropdownMenuShortcutProps,
  ContextMenuSubProps as DropdownMenuSubProps,
  ContextMenuSubTriggerProps as DropdownMenuSubTriggerProps,
  ContextMenuSubContentProps as DropdownMenuSubContentProps,
} from "./context-menu";
